import React, { useCallback } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import ProtectedRoute from "@/shared/components/ProtectedRoute";
import PushNotificationsBootstrap from "@/shared/components/PushNotificationsBootstrap";
import StandaloneRedirect from "@/shared/components/StandaloneRedirect";
import { RouterTransition } from "@/shared/motion";
import { useAuth } from "@/shared/context/AuthContext";

import DriverRouteGuard from "@/apps/driver/DriverRouteGuard";
import DriverSubdomainRedirect from "@/apps/driver/DriverSubdomainRedirect";
import DriverAppPage from "@/apps/driver/pages/DriverApp";
import DriverMarketplace from "@/apps/driver/pages/DriverMarketplace";
import DriverShell from "@/apps/driver/components/DriverShell";

/**
 * Feb 2026 · Non-drivers must not render any /driver surface. The
 * marketplace previously mounted for one tick, fired its data fetches
 * (which the backend correctly 403'd via `_require_driver`), and then
 * DriverRouteGuard's effect redirected — leaving a confusing "Driver
 * access required" toast on a marketplace shell. This synchronous
 * gate short-circuits before the child mounts so no fetch is issued.
 */
function DriverOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_driver) return <Navigate to="/" replace />;
  return children;
}

export default function DriverApp() {
  const renderRoutes = useCallback((loc) => (
    <Routes location={loc}>
      <Route path="/driver"          element={<ProtectedRoute><DriverOnlyRoute><DriverMarketplace /></DriverOnlyRoute></ProtectedRoute>} />
      <Route path="/driver/assigned" element={<ProtectedRoute><DriverOnlyRoute><DriverAppPage /></DriverOnlyRoute></ProtectedRoute>} />
      {/* Legacy sub-pages now live inside the AccountDrawer */}
      <Route path="/driver/*"        element={<Navigate to="/driver" replace />} />
    </Routes>
  ), []);
  return (
    <>
      <StandaloneRedirect />
      <DriverSubdomainRedirect />
      <DriverRouteGuard />
      <PushNotificationsBootstrap />
      <DriverShell>
        <RouterTransition routes={renderRoutes} />
      </DriverShell>
    </>
  );
}
